import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase/client';
import './InvestorConsultas.css';

interface Consulta {
  id: string;
  fecha_hora: string;
  duracion: number;
  modalidad: string;
  estado: string;
  tipo_consulta: string;
  paciente_id: string;
  nombre_paciente: string;
  terapeuta_id: string;
  nombre_terapeuta: string;
}

interface InvestorConsultasProps {
  investor: { id: string };
}

const ESTADO_LABEL: Record<string, string> = {
  programada:  'Programada',
  confirmada:  'Confirmada',
  en_progreso: 'En progreso',
  completada:  'Completada',
  cancelada:   'Cancelada',
  no_asistio:  'No asistió',
};

const TIPO_LABEL: Record<string, string> = {
  individual: 'Individual',
  pareja:     'Pareja',
  familiar:   'Familiar',
  sexologia:  'Sexología',
};

const MODALIDAD_LABEL: Record<string, string> = {
  presencial: 'Presencial',
  online:     'Online',
  domicilio:  'Domicilio',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const InvestorConsultas: React.FC<InvestorConsultasProps> = ({ investor }) => {
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo]     = useState('');

  // Default: últimos 3 meses → próximo mes
  const [desde, setDesde] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return toDateInput(d);
  });
  const [hasta, setHasta] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return toDateInput(d);
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('get_investor_calendar', {
      p_inversionista_id: investor.id,
      p_desde: desde || undefined,
      p_hasta: hasta || undefined,
    });
    if (rpcError) {
      console.error('Error cargando consultas inversor:', rpcError.code);
      setError('No se pudo cargar las consultas.');
    } else {
      setConsultas((data as Consulta[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [investor.id, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return consultas.filter(c => {
      const matchSearch = !q
        || c.nombre_paciente.toLowerCase().includes(q)
        || c.nombre_terapeuta.toLowerCase().includes(q);
      const matchEstado = !filterEstado || c.estado === filterEstado;
      const matchTipo   = !filterTipo   || c.tipo_consulta === filterTipo;
      return matchSearch && matchEstado && matchTipo;
    });
  }, [consultas, search, filterEstado, filterTipo]);

  // Stats
  const stats = useMemo(() => {
    const total     = filtered.length;
    const proximas  = filtered.filter(c => c.estado === 'programada').length;
    const completadas = filtered.filter(c => c.estado === 'completada').length;
    const canceladas  = filtered.filter(c => c.estado === 'cancelada' || c.estado === 'no_asistio').length;
    return { total, proximas, completadas, canceladas };
  }, [filtered]);

  const statCard = (label: string, value: number, tone: string, icon: string) => (
    <div className="icn-stat-card">
      <div className="icn-stat-card__row">
        {/* El tono (color de ícono + tinte de fondo) lo resuelve
            .icn-stat-card__icon--{tone} en InvestorConsultas.css con tokens
            de Contexto B — nada de color inline. */}
        <div className={`icn-stat-card__icon icn-stat-card__icon--${tone}`}>
          <i className={icon}></i>
        </div>
        <span className="icn-stat-card__label">{label}</span>
      </div>
      <div className="icn-stat-card__value">
        {value}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando consultas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-management investor-consultas">
      {/* Header */}
      <div className="icn-header">
        <h2 className="icn-header__title">
          Consultas
        </h2>
        <p className="icn-header__subtitle">
          Visión global de las consultas de tus pacientes referidos
        </p>
      </div>

      {/* Stats — el tono de cada card lo resuelve InvestorConsultas.css vía
          .icn-stat-card__icon--{tone} con tokens de Contexto B (theme-aware). */}
      <div className="icn-stats">
        {statCard('Total',       stats.total,        'total',       'fas fa-clipboard-list')}
        {statCard('Próximas',    stats.proximas,     'proximas',    'fas fa-clock')}
        {statCard('Completadas', stats.completadas,  'completadas', 'fas fa-check-circle')}
        {statCard('Canceladas',  stats.canceladas,   'canceladas',  'fas fa-times-circle')}
      </div>

      {/* Filtros */}
      <div className="icn-filters">
        {/* Búsqueda */}
        <div className="icn-search">
          <i className="fas fa-search icn-search__icon"></i>
          <input
            type="text"
            placeholder="Buscar paciente o terapeuta..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="icn-search__input"
          />
        </div>

        {/* Estado */}
        <select
          value={filterEstado}
          onChange={e => setFilterEstado(e.target.value)}
          className="icn-select"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {/* Tipo */}
        <select
          value={filterTipo}
          onChange={e => setFilterTipo(e.target.value)}
          className="icn-select"
        >
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {/* Rango de fechas */}
        <div className="icn-date-range">
          <input
            type="date"
            value={desde}
            onChange={e => setDesde(e.target.value)}
            className="icn-date-input"
          />
          <span className="icn-date-range__arrow">→</span>
          <input
            type="date"
            value={hasta}
            onChange={e => setHasta(e.target.value)}
            className="icn-date-input"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="icn-error-banner">
          {error}
        </div>
      )}

      {/* Vacío */}
      {!error && filtered.length === 0 && (
        <div className="icn-empty">
          <i className="fas fa-calendar-times icn-empty__icon"></i>
          {consultas.length === 0
            ? 'No hay consultas registradas en el período seleccionado.'
            : 'Sin resultados para los filtros aplicados.'}
        </div>
      )}

      {/* Lista */}
      <div className="icn-list">
        {filtered.map(c => {
          const isPast = new Date(c.fecha_hora) < new Date();
          const isMuted = c.estado === 'cancelada' || c.estado === 'no_asistio';
          return (
            <div key={c.id} className={`icn-row${isMuted ? ' icn-row--muted' : ''}`}>
              {/* Fecha */}
              <div className="icn-row__date">
                <div className={`icn-row__day${isPast ? '' : ' icn-row__day--upcoming'}`}>
                  {new Date(c.fecha_hora).getDate()}
                </div>
                <div className="icn-row__month">
                  {new Date(c.fecha_hora).toLocaleDateString('es-CL', { month: 'short', year: '2-digit' })}
                </div>
                <div className="icn-row__time">
                  {formatTime(c.fecha_hora)}
                </div>
              </div>

              {/* Detalle */}
              <div className="icn-row__detail">
                <div className="icn-row__detail-top">
                  <span className="icn-row__patient">
                    {c.nombre_paciente}
                  </span>
                  <span className={`icn-badge icn-badge--${c.estado}`}>
                    {ESTADO_LABEL[c.estado] ?? c.estado}
                  </span>
                </div>
                <div className="icn-row__meta">
                  <span className="icn-row__meta-therapist">
                    <i className="fas fa-user-md icn-row__meta-icon"></i>
                    {c.nombre_terapeuta}
                  </span>
                  <span className="icn-row__meta-item">
                    <i className="fas fa-tag icn-row__meta-icon"></i>
                    {TIPO_LABEL[c.tipo_consulta] ?? c.tipo_consulta}
                  </span>
                  <span className="icn-row__meta-item">
                    <i className="fas fa-clock icn-row__meta-icon"></i>
                    {c.duracion} min
                  </span>
                  <span className="icn-row__meta-item">
                    <i className={`fas fa-${c.modalidad === 'online' ? 'video' : 'map-marker-alt'} icn-row__meta-icon`}></i>
                    {MODALIDAD_LABEL[c.modalidad] ?? c.modalidad}
                  </span>
                </div>
              </div>

              {/* Fecha completa — sólo para contexto visual en mobile */}
              <div className="icn-row__aside">
                <span className="icn-row__aside-date">
                  {formatDate(c.fecha_hora)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InvestorConsultas;
